import React, { useEffect, useId, useRef } from 'react';
import { getProductBundle, type StorefrontBundle, type StorefrontBundleTier } from '../../api/client';
import { cartMoney } from '../../commerce/cartMoney';
import { useStoreRead } from './useStoreRead';
import type { Purchase } from './usePurchase';
import './elementOfferLadder.css';

// The merchant's quantity offer on a product page — "1 pair, 2 pairs −10%, 3 pairs −20%" — drawn above the buy
// buttons as bundle apps draw it on Shopify: one card per rung with the server's price, the old one struck, and the
// merchant's badge. Choosing a rung sets the quantity, and checkout prices that quantity at the rung's price, so the
// page never does money maths of its own. The rung the merchant pre-selected is chosen once the offer arrives.

/** One rung as the ladder draws it: the merchant's words and the server's prices. */
type Rung = StorefrontBundleTier;

// Reads the product's active offer, priced against the variant being ordered, or nothing for a sample or no offer.
function useProductOffer(buy: Purchase): StorefrontBundle | null {
  const productId = buy.product && !buy.sample ? buy.product.id : null;
  const variantId = buy.variant?.id;
  const read = useStoreRead(productId ? `offer-ladder:${productId}:${variantId ?? ''}` : null, () => getProductBundle(productId!, variantId));
  return read.data && read.data.tiers.length > 1 ? read.data : null;
}

// Chooses the merchant's pre-selected rung once per offer, unless the shopper already picked a quantity of their own.
function usePreselect(offer: StorefrontBundle | null, buy: Purchase): void {
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (!offer || done.current === offer.id) return;
    done.current = offer.id;
    const preferred = offer.tiers.find((rung) => rung.isDefault);
    if (preferred && buy.quantity === 1 && preferred.quantity !== 1) buy.setQuantity(preferred.quantity);
  }, [offer, buy]);
}

/** What one rung's card needs: the rung, whether it is chosen, how money is spelled, and how to choose it. */
type RungProps = { rung: Rung; group: string; chosen: boolean; money: (amount: number) => string; onChoose: () => void };

// Draws one rung as a card around a real radio, so arrows move between rungs and a screen reader hears "2 of 3".
const RungCard: React.FC<RungProps> = ({ rung, group, chosen, money, onChoose }) => (
  <label className="xe-ladder-rung" data-chosen={chosen ? '' : undefined}>
    <input type="radio" name={group} className="xe-ladder-radio" checked={chosen} onChange={onChoose} />
    {rung.badgeText ? <span className="xe-ladder-badge" dir="auto">{rung.badgeText}</span> : null}
    <span className="xe-ladder-words">
      <span className="xe-ladder-label" dir="auto">{rung.tierLabel}</span>
      {rung.subtext ? <span className="xe-ladder-sub" dir="auto">{rung.subtext}</span> : null}
    </span>
    {rung.pricing.savingsPercent > 0 ? <span className="xe-ladder-save" dir="ltr">−{Math.round(rung.pricing.savingsPercent)}%</span> : null}
    <span className="xe-ladder-prices">
      <span className="xe-ladder-price">{money(rung.pricing.lineTotal)}</span>
      {rung.pricing.savingsAmount > 0 ? <s className="xe-ladder-was">{money(rung.pricing.compareAtTotal)}</s> : null}
    </span>
  </label>
);

// Draws the product's quantity offer, or nothing when the store runs none on it.
export const OfferLadder: React.FC<{ buy: Purchase }> = ({ buy }) => {
  const offer = useProductOffer(buy);
  const group = useId();
  usePreselect(offer, buy);
  if (!offer) return null;
  const currency = buy.commerce?.currency ?? 'MAD';
  const direction = buy.commerce?.direction ?? 'ltr';
  // Spells an amount the way the order form will.
  const money = (amount: number) => cartMoney(amount, currency, direction);
  return (
    <fieldset className="xe-ladder">
      <legend className="xe-ladder-title" dir="auto">{offer.title}</legend>
      {offer.subtitle ? <p className="xe-ladder-subtitle" dir="auto">{offer.subtitle}</p> : null}
      {offer.tiers.map((rung) => (
        <RungCard key={rung.id} rung={rung} group={group} chosen={buy.quantity === rung.quantity} money={money}
          onChoose={() => { if (!buy.locked) buy.setQuantity(rung.quantity); }} />
      ))}
    </fieldset>
  );
};
